import React from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {documentosAdjuntosApi} from '../../../api/api';
import type {DocumentoAdjuntoAmbito, DocumentoAdjuntoDTO} from '../../../api/Objetos';
import {useNotifications} from '../../../ui/notifications';
import {ApiError} from '../../../api/http';
import {useProjectReadOnly} from './projectForm.helpers';

export function DocumentosAdjuntosPanel(props: {
    proyectoId: number;
    ambito: DocumentoAdjuntoAmbito;
    subcontratistaId?: number;
    disabled?: boolean;
    accept?: string;
    maxFiles?: number;
    imagePreview?: boolean;
    compact?: boolean;
}) {
    const {t} = useTranslation();
    const readOnly = useProjectReadOnly();
    const {showSuccess, showError} = useNotifications();
    const queryClient = useQueryClient();
    const [nombre, setNombre] = React.useState('');
    const [descripcion, setDescripcion] = React.useState('');
    const [fecha, setFecha] = React.useState(new Date().toISOString().slice(0, 10));
    const [archivo, setArchivo] = React.useState<File | null>(null);
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const [selectedPreview, setSelectedPreview] = React.useState('');
    const queryKey = ['documentos-adjuntos', props.proyectoId, props.ambito, props.subcontratistaId ?? null];
    const enabled = !props.disabled && (props.ambito !== 'SUBCONTRATISTA' || Boolean(props.subcontratistaId));
    const documentos = useQuery<DocumentoAdjuntoDTO[]>({
        queryKey,
        queryFn: () => documentosAdjuntosApi.listar(props.proyectoId, props.ambito, props.subcontratistaId),
        enabled,
    });
    const limitReached = Boolean(props.maxFiles && (documentos.data?.length ?? 0) >= props.maxFiles);
    React.useEffect(() => {
        if (!archivo || !props.imagePreview) {
            setSelectedPreview('');
            return;
        }
        const url = URL.createObjectURL(archivo);
        setSelectedPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [archivo, props.imagePreview]);
    const subir = useMutation({
        mutationFn: () => documentosAdjuntosApi.subir(props.proyectoId, {
            ambito: props.ambito,
            subcontratistaId: props.subcontratistaId,
            nombre,
            descripcion,
            fecha,
            archivo: archivo!,
        }),
        onSuccess: async () => {
            setNombre('');
            setDescripcion('');
            setArchivo(null);
            if (inputRef.current) inputRef.current.value = '';
            await queryClient.invalidateQueries({queryKey});
            showSuccess(t('documentosAdjuntos.uploaded'));
        },
        onError: error => showError(t('documentosAdjuntos.uploadError'), errorMessage(error)),
    });
    const eliminar = useMutation({
        mutationFn: (id: number) => documentosAdjuntosApi.eliminar(props.proyectoId, id),
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey});
            showSuccess(t('documentosAdjuntos.deleted'));
        },
        onError: error => showError(t('documentosAdjuntos.deleteError'), errorMessage(error)),
    });

    const abrir = async (id: number, filename: string, download: boolean) => {
        const preview = download ? null : window.open('', '_blank');
        try {
            const blob = await documentosAdjuntosApi.contenido(props.proyectoId, id, download);
            const url = URL.createObjectURL(blob);
            if (download) {
                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                link.click();
            } else if (preview) {
                preview.location.href = url;
            }
            window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (error) {
            preview?.close();
            showError(t('documentosAdjuntos.openError'), errorMessage(error));
        }
    };

    if (!enabled) {
        return <div className="adminEmptyState">{t('documentosAdjuntos.saveSubcontractorFirst')}</div>;
    }

    return (
        <div className="documentUploadPanel">
            {!readOnly && !limitReached && <form className="documentUploadForm" onSubmit={event => {
                event.preventDefault();
                if (!archivo) {
                    showError(t('documentosAdjuntos.uploadError'), t('documentosAdjuntos.fileRequired'));
                    return;
                }
                subir.mutate();
            }}>
                {!props.compact && <label className="label">
                    {t('documentosAdjuntos.name')}
                    <input className="input" value={nombre} onChange={event => setNombre(event.target.value)}/>
                </label>}
                {!props.compact && <label className="label">
                    {t('documentosAdjuntos.date')}
                    <input className="input" type="date" value={fecha} onChange={event => setFecha(event.target.value)} required/>
                </label>}
                {!props.compact && <label className="label documentUploadDescription">
                    {t('documentosAdjuntos.description')}
                    <input className="input" value={descripcion} onChange={event => setDescripcion(event.target.value)}/>
                </label>}
                <label className="label documentUploadFile">
                    {t('documentosAdjuntos.file')}
                    <input ref={inputRef} className="input" type="file" accept={props.accept ?? ".pdf,.png,.jpg,.jpeg,.webp,.txt,.csv,.doc,.docx,.xls,.xlsx,.odt,.ods"} onChange={event => setArchivo(event.target.files?.[0] ?? null)} required/>
                </label>
                {selectedPreview ? <img className="documentImagePreview" src={selectedPreview} alt="Vista previa de la imagen seleccionada"/> : null}
                <button className="btn btnPrimary" type="submit" disabled={subir.isPending}>
                    {subir.isPending ? t('documentosAdjuntos.uploading') : props.imagePreview ? 'Añadir imagen' : props.accept === '.pdf' ? 'Añadir PDF' : t('documentosAdjuntos.upload')}
                </button>
            </form>}

            {documentos.isLoading ? <div className="muted">{t('documentosAdjuntos.loading')}</div> : null}
            {documentos.isError ? <div className="alert alertError">{errorMessage(documentos.error)}</div> : null}
            {!documentos.isLoading && (documentos.data?.length ?? 0) === 0 ? <div className="adminEmptyState">{t('documentosAdjuntos.empty')}</div> : null}
            <div className="documentList">
                {(documentos.data ?? []).map((documento: DocumentoAdjuntoDTO) => (
                    <div className="documentListItem" key={documento.id}>
                        {props.imagePreview ? <DocumentImagePreview proyectoId={props.proyectoId} documento={documento}/> : null}
                        <div className="documentListInfo">
                            <strong>{documento.nombre}</strong>
                            <span>{documento.fecha} · {documento.nombreArchivo} · {formatSize(documento.tamano)}</span>
                            {documento.descripcion ? <span>{documento.descripcion}</span> : null}
                        </div>
                        <div className="documentListActions">
                            <button className="btn btnSmall btnGhost" type="button" onClick={() => void abrir(documento.id!, documento.nombreArchivo, false)}>{t('documentosAdjuntos.view')}</button>
                            <button className="btn btnSmall btnSecondary" type="button" onClick={() => void abrir(documento.id!, documento.nombreArchivo, true)}>{t('documentosAdjuntos.download')}</button>
                            {!readOnly && <button className="btn btnSmall btnDanger" type="button" disabled={eliminar.isPending} onClick={() => {
                                if (window.confirm(t('documentosAdjuntos.confirmDelete', {name: documento.nombre}))) eliminar.mutate(documento.id!);
                            }}>{t('documentosAdjuntos.delete')}</button>}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function DocumentImagePreview({proyectoId, documento}: {proyectoId: number; documento: DocumentoAdjuntoDTO}) {
    const [url, setUrl] = React.useState('');
    React.useEffect(() => {
        let active = true;
        let objectUrl = '';
        documentosAdjuntosApi.contenido(proyectoId, documento.id!, false).then(blob => {
            objectUrl = URL.createObjectURL(blob);
            if (active) setUrl(objectUrl);
        }).catch(() => undefined);
        return () => {
            active = false;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [proyectoId, documento.id]);
    return url ? <img className="documentImagePreview" src={url} alt={documento.nombre}/> : null;
}

function formatSize(bytes: number) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function errorMessage(error: unknown) {
    if (error instanceof ApiError) return error.message.replace(/^\[\d+]\s*/, '');
    return error instanceof Error ? error.message : String(error);
}
